import axios, { AxiosInstance } from 'axios'
import { HAPTTIC_X_API_KEY } from 'src/constants/env'
import { getSessionIdFromLocalStorage } from 'src/localStorage/sessionId/getSessionIdFromLocalStorage/getSessionIdFromLocalStorage'
import { ANALYTICS_API_URL } from 'src/services/constants/urls'
import {
  ContentAnalysisActiveDateRange,
  ContentAnalysisDeleteResponse,
  ContentAnalysisListResponse,
  ContentAnalysisPayload,
  ContentAnalysisSingleResponse,
} from './interfaces/contentAnalysis.interface'

const getHeaders = () => ({
  'Content-Type': 'application/json',
  'X-Api-Key': HAPTTIC_X_API_KEY,
  'x-session-id': getSessionIdFromLocalStorage() || '',
})

class ContentAnalysisApiService {
  private static _instance: ContentAnalysisApiService

  private client: AxiosInstance

  private constructor() {
    this.client = axios.create({
      baseURL: ANALYTICS_API_URL,
      headers: getHeaders(),
    })
  }

  private static get instance(): ContentAnalysisApiService {
    if (!ContentAnalysisApiService._instance) {
      ContentAnalysisApiService._instance = new ContentAnalysisApiService()
    }
    return ContentAnalysisApiService._instance
  }

  public static refresh(): void {
    ContentAnalysisApiService._instance = new ContentAnalysisApiService()
  }

  public static async getList(params?: {
    fromDate?: string
    toDate?: string
  }): Promise<ContentAnalysisListResponse> {
    const response =
      await ContentAnalysisApiService.instance.client.get<ContentAnalysisListResponse>(
        '/v1_private/content-analysis',
        { headers: getHeaders(), params }
      )
    return response.data
  }

  public static async getById(
    id: string
  ): Promise<ContentAnalysisSingleResponse> {
    const response =
      await ContentAnalysisApiService.instance.client.get<ContentAnalysisSingleResponse>(
        `/v1_private/content-analysis/${id}`,
        { headers: getHeaders() }
      )
    return response.data
  }

  public static async create(
    payload: ContentAnalysisPayload
  ): Promise<ContentAnalysisSingleResponse> {
    const response =
      await ContentAnalysisApiService.instance.client.post<ContentAnalysisSingleResponse>(
        '/v1_private/content-analysis',
        payload,
        { headers: getHeaders() }
      )
    return response.data
  }

  public static async update(
    id: string,
    payload: ContentAnalysisPayload
  ): Promise<ContentAnalysisSingleResponse> {
    const response =
      await ContentAnalysisApiService.instance.client.put<ContentAnalysisSingleResponse>(
        `/v1_private/content-analysis/${id}`,
        payload,
        { headers: getHeaders() }
      )
    return response.data
  }

  public static async updateCommentsSentiment<T>(
    id: string,
    payload: {
      commentIds: string[]
      sentiment: 'negative' | 'neutral' | 'positive'
    }
  ): Promise<T> {
    const response = await ContentAnalysisApiService.instance.client.patch<T>(
      `/v1_private/content-analysis/${id}/comments/sentiment`,
      payload,
      { headers: getHeaders() }
    )
    return response.data
  }

  public static async delete(
    id: string
  ): Promise<ContentAnalysisDeleteResponse> {
    const response =
      await ContentAnalysisApiService.instance.client.delete<ContentAnalysisDeleteResponse>(
        `/v1_private/content-analysis/${id}`,
        { headers: getHeaders() }
      )
    return response.data
  }

  public static async getActivityFrequency<T>(
    id: string,
    params?: {
      fromDate?: string
      toDate?: string
    }
  ): Promise<T> {
    const response = await ContentAnalysisApiService.instance.client.get<T>(
      `/v1_private/content-analysis/${id}/activity-frequency`,
      { headers: getHeaders(), params }
    )
    return response.data
  }

  public static async getComments<T>(
    id: string,
    params?: {
      fromDate?: string
      toDate?: string
    }
  ): Promise<T> {
    const response = await ContentAnalysisApiService.instance.client.get<T>(
      `/v1_private/content-analysis/${id}/comments`,
      { headers: getHeaders(), params }
    )
    return response.data
  }

  public static async getContentPerformance<T>(
    id: string,
    params?: {
      fromDate?: string
      toDate?: string
    }
  ): Promise<T> {
    const response = await ContentAnalysisApiService.instance.client.get<T>(
      `/v1_private/content-analysis/${id}/content-performance`,
      { headers: getHeaders(), params }
    )
    return response.data
  }

  public static async getEngagementInsights<T>(
    id: string,
    params?: {
      fromDate?: string
      toDate?: string
    }
  ): Promise<T> {
    const response = await ContentAnalysisApiService.instance.client.get<T>(
      `/v1_private/content-analysis/${id}/engagement-insights`,
      { headers: getHeaders(), params }
    )
    return response.data
  }

  // Without an id: the bounds across every analysis the user owns (the
  // listing page's picker); with one: that analysis's own bounds.
  public static async getActiveDateRange(
    id?: string
  ): Promise<ContentAnalysisActiveDateRange> {
    const path = id
      ? `/v1_private/content-analysis/${id}/active-date-range`
      : '/v1_private/content-analysis/active-date-range'
    const response =
      await ContentAnalysisApiService.instance.client.get<ContentAnalysisActiveDateRange>(
        path,
        { headers: getHeaders() }
      )
    return response.data
  }

  public static async getIndividualPosts<T>(
    id: string,
    params?: {
      fromDate?: string
      limit?: number
      page?: number
      platform?: string
      toDate?: string
    }
  ): Promise<T> {
    const response = await ContentAnalysisApiService.instance.client.get<T>(
      `/v1_private/content-analysis/${id}/individual-posts`,
      { headers: getHeaders(), params }
    )
    return response.data
  }

  public static async exportPosts(
    id: string,
    params?: {
      fromDate?: string
      platform?: string
      toDate?: string
    }
  ): Promise<Blob> {
    const response = await ContentAnalysisApiService.instance.client.get<Blob>(
      `/v1_private/content-analysis/${id}/export`,
      { headers: getHeaders(), params, responseType: 'blob' }
    )
    return response.data
  }

  public static async getSmartInsights<T>(
    id: string,
    params?: {
      fromDate?: string
      toDate?: string
    }
  ): Promise<T> {
    const response = await ContentAnalysisApiService.instance.client.get<T>(
      `/v1_private/content-analysis/${id}/smart-insights`,
      { headers: getHeaders(), params }
    )
    return response.data
  }

  // fromDate/toDate: the page's range, so the response carries that period's
  // summary and its own cooldown.
  public static async getSummaries<T>(
    id: string,
    params?: { fromDate?: string; toDate?: string }
  ): Promise<T> {
    const response = await ContentAnalysisApiService.instance.client.get<T>(
      `/v1_private/content-analysis/${id}/summary`,
      { headers: getHeaders(), params }
    )
    return response.data
  }

  public static async generateSummary<T>(
    id: string,
    payload: {
      fromDate: string
      toDate: string
    }
  ): Promise<T> {
    const response = await ContentAnalysisApiService.instance.client.post<T>(
      `/v1_private/content-analysis/${id}/summary`,
      payload,
      { headers: getHeaders() }
    )
    return response.data
  }
}

export { ContentAnalysisApiService }
